import "@nim-os/in2-app-talk";
import { ThemeProvider } from "@ls/ls-style";
import App from "./App";
import { Provider as StoreProvider } from "react-redux";
import { UserProvider, LegacyAuthProvider } from "@ls/user-module";
import DateProvider from "@ls/date-module";
import store from "state/store";
import { ErrorBoundary } from "@ls/error-module";
import { PromptProvider } from "@ls/react-prompt";
import { Provider as ToastProvider } from "@ls/react-toast";
import { DistributionProvider } from "@ls/distribution";
import { PersistenceProvider } from "@ls/persistence-module";
import { EntityServiceProvider } from "@ls/entity-service";
import { EntityMappingServiceProvider } from "@ls/entity-mapping-service";
import { GroupingServiceProvider } from "@ls/grouping-service";
import { QueryClient, QueryClientProvider } from "react-query";
import { BrowserRouter as Router } from "react-router-dom";
import React from "react";
import { render } from "react-dom";
import { setFetcher } from "@ls/js-fetch";

console.info("IN2_ENV:", process.env.REACT_APP_IN2_ENV);
console.info("UI_VERSION:", process.env.REACT_APP_UI_VERSION);
console.info("SERVER_VERSION:", process.env.REACT_APP_SERVER_VERSION);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 1000 * 60 * 60 * 2, //ms * second * min * hours
    },
  },
});
const appId = process.env.REACT_APP_ID;
if (
  process.env.REACT_APP_HOST_ENV === "AZURE" &&
  !process.env.REACT_APP_ALLOWED_TENANT_IDS
) {
  throw new Error("REACT_APP_ALLOWED_TENANT_IDS is not defined");
}

const allowedTenantIds = process.env.REACT_APP_ALLOWED_TENANT_IDS?.split(
  ","
).map((tid) => tid.trim());

const hostEnv = process.env.REACT_APP_HOST_ENV;
console.log("process.env.REACT_APP_HOST_ENV", hostEnv);

// Initialize the app after MSAL is ready
const initializeApp = async () => {
  console.log(
    "Starting app...",
    process.env.REACT_APP_ID,
    process.env.REACT_APP_BASE_URL_ENTITY_SERVICE,
    process.env.REACT_APP_BASE_URL_ENTITY_MAPPING
  );
  let Auth;
  if (process.env.REACT_APP_HOST_ENV === "AZURE") {
    Auth = function Auth(props) {
      return (
        <UserProvider
          allowedTenantIds={allowedTenantIds}
          setFetcher={setFetcher}
        >
          {props.children}
        </UserProvider>
      );
    };
  } else {
    Auth = function Auth(props) {
      return <LegacyAuthProvider>{props.children}</LegacyAuthProvider>;
    };
  }

  const renderApp = () => {
    render(
      <Auth>
        <DistributionProvider>
          <StoreProvider store={store}>
            <ErrorBoundary>
              <ThemeProvider>
                <EntityServiceProvider
                  appId={appId}
                  baseURL={process.env.REACT_APP_BASE_URL_ENTITY_SERVICE}
                >
                  <EntityMappingServiceProvider
                    appId={appId}
                    baseURL={process.env.REACT_APP_BASE_URL_ENTITY_MAPPING}
                  >
                    <GroupingServiceProvider
                      appId={appId}
                      baseURL={process.env.REACT_APP_BASE_URL_GROUPING_SERVICE}
                    >
                      <PersistenceProvider
                        appId={appId}
                        baseURL={process.env.REACT_APP_BASE_URL_PERSISTENCE}
                      >
                        <DateProvider
                          appId={appId}
                          baseURL={
                            process.env.REACT_APP_BASE_URL_ENTITY_SERVICE
                          }
                        >
                          <PromptProvider>
                            <ToastProvider>
                              <Router>
                                <QueryClientProvider client={queryClient}>
                                  <App />
                                </QueryClientProvider>
                              </Router>
                            </ToastProvider>
                          </PromptProvider>
                        </DateProvider>
                      </PersistenceProvider>
                    </GroupingServiceProvider>
                  </EntityMappingServiceProvider>
                </EntityServiceProvider>
              </ThemeProvider>
            </ErrorBoundary>
          </StoreProvider>
        </DistributionProvider>
      </Auth>,
      document.getElementById("root")
    );
  };
  await renderApp();
};

// Initialize the app
initializeApp().catch(console.error);

if (process.env.REACT_APP_IN2_ENV !== "PRD") {
  const el = document.createElement("span");
  el.appendChild(document.createTextNode(process.env.REACT_APP_IN2_ENV));
  el.setAttribute(
    "style",
    "color:#f0f71b;position:fixed;z-index:100;top:2px;left:2px;font-size:1.6em;font-weight:bold;"
  );
  el.setAttribute("id", "env-warn");
  document.body.insertBefore(el, document.getElementById("root"));
}
